import React, { useState, useRef, ChangeEvent, KeyboardEvent } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import PhotoUploadGrid from '../../components/PhotoUploadGrid';
import { useOverlayClose } from '../../components/useOverlayClose';

const API_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';

// ─── Types ───────────────────────────────────────────────────────────────────

interface ExistingConsumable {
  id: number;
  name: string;
  category: string | null;
  gst_percent: number | null;
  low_stock_alert_quantity: number;
}

interface CatalogForm {
  name: string;
  category: string;
  priority: string;
  gst_percent: string;
  low_stock_alert_quantity: string;
}

interface BatchForm {
  vendor_name: string;
  batch_no: string;
  hsn_sac: string;
  purchase_inv_no: string;
  purchase_inv_date: string;
  expiry_date: string;
  mrp: string;
  dealer_price: string;
  selling_price: string;
  initial_quantity: string;
}

interface Props {
  onClose: () => void;
  onSuccess: () => void;
  existingConsumables?: ExistingConsumable[];
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles: { [key: string]: React.CSSProperties } = {
  overlay:      { position: 'fixed', inset: 0, backgroundColor: 'rgba(15, 23, 42, 0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(4px)' },
  modalWrapper: { backgroundColor: 'white', borderRadius: '12px', width: '98%', maxWidth: '720px', height: '88vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' },
  mainArea:     { flex: 1, display: 'flex', flexDirection: 'column', backgroundColor: '#fff', overflow: 'hidden' },
  scrollArea:   { flex: 1, overflowY: 'auto', padding: '1.5rem 2.5rem' },
  sectionBlock: { marginBottom: '3rem' },
  sectionTitle: { fontSize: '1rem', fontWeight: 700, color: '#0f172a', margin: '0 0 1.25rem 0', paddingBottom: '0.5rem', borderBottom: '1px solid #e2e8f0' },
  label:        { display: 'block', fontSize: '0.75rem', color: '#64748b', fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '0.5px', marginBottom: '0.3rem' },
  input:        { padding: '0.6rem', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.85rem', boxSizing: 'border-box' as const, height: '40px', width: '100%' },
  footer:       { padding: '1rem 2.5rem', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', backgroundColor: '#f8fafc' },
  button:       { padding: '0.6rem 1.5rem', fontSize: '0.875rem', fontWeight: 600, borderRadius: '6px', border: 'none', cursor: 'pointer' },
  cancelButton: { backgroundColor: '#e2e8f0', color: '#475569' },
  saveButton:   { backgroundColor: '#2563eb', color: 'white' },
};

const fieldStyle: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: '0.3rem' };
const gridGap = { gap: '0.9rem', marginBottom: '0.9rem' };

// ─── Name Combobox ────────────────────────────────────────────────────────────

interface NameComboboxProps {
  value: string;
  existingConsumables: ExistingConsumable[];
  selectedExisting: ExistingConsumable | null;
  nextInputRef?: React.RefObject<HTMLInputElement | null>;
  onChange: (val: string) => void;
  onSelectExisting: (item: ExistingConsumable) => void;
  onClearExisting: () => void;
}

const NameCombobox: React.FC<NameComboboxProps> = ({
  value, existingConsumables, selectedExisting, nextInputRef,
  onChange, onSelectExisting, onClearExisting,
}) => {
  const [isFocused, setIsFocused] = useState(false);

  const filtered = existingConsumables.filter(c =>
    c.name.toLowerCase().includes(value.toLowerCase())
  );
  const isExactMatch = existingConsumables.some(
    c => c.name.toLowerCase() === value.trim().toLowerCase()
  );
  const showDropdown = isFocused && (filtered.length > 0 || (value.trim().length > 0 && !isExactMatch));

  const handleInput = (e: ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    onChange(v);
    if (selectedExisting && v !== selectedExisting.name) onClearExisting();
  };

  const handleSelect = (item: ExistingConsumable) => {
    onChange(item.name);
    onSelectExisting(item);
    setIsFocused(false);
    setTimeout(() => nextInputRef?.current?.focus(), 0);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    if (showDropdown && filtered.length > 0) {
      handleSelect(filtered[0]);
    } else if (value.trim()) {
      setIsFocused(false);
      setTimeout(() => nextInputRef?.current?.focus(), 0);
    }
  };

  return (
    <div style={{ position: 'relative' }}>
      <input
        value={value}
        onChange={handleInput}
        onKeyDown={handleKeyDown}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setTimeout(() => setIsFocused(false), 200)}
        placeholder="e.g. Gauze Pads 4×4"
        autoComplete="off"
        autoFocus
        style={{ ...styles.input, outline: 'none', borderColor: isFocused ? '#93c5fd' : '#cbd5e1' }}
      />

      {showDropdown && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1000, backgroundColor: 'white', border: '1px solid #cbd5e1', borderRadius: '6px', marginTop: '2px', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)', maxHeight: 220, overflowY: 'auto' }}>
          {value.trim().length > 0 && !isExactMatch && (
            <div style={{ padding: '0.5rem 0.75rem', fontSize: '0.82rem', color: '#16a34a', fontWeight: 700, borderBottom: filtered.length > 0 ? '1px solid #f1f5f9' : 'none', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <span style={{ fontSize: '1rem' }}>+</span> Create &ldquo;{value.trim()}&rdquo; as new consumable
            </div>
          )}
          {filtered.map(item => (
            <div
              key={item.id}
              onMouseDown={e => { e.preventDefault(); handleSelect(item); }}
              style={{ padding: '0.5rem 0.75rem', cursor: 'pointer', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
              onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#f8fafc')}
              onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'white')}
            >
              <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>{item.name}</div>
              <span style={{ fontSize: '0.68rem', fontWeight: 700, background: '#dcfce7', color: '#15803d', borderRadius: '4px', padding: '0.1rem 0.45rem', whiteSpace: 'nowrap' }}>
                + Add Stock
              </span>
            </div>
          ))}
        </div>
      )}

      {selectedExisting && (
        <div style={{ marginTop: '0.5rem', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: '6px', padding: '0.5rem 0.75rem', fontSize: '0.8rem', color: '#15803d', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>✓ Existing consumable — catalog fields pre-filled. Fill in the stock details below.</span>
          <button
            type="button"
            onClick={() => { onClearExisting(); onChange(''); }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', fontSize: '1rem', lineHeight: 1, padding: '0 0.2rem' }}
          >
            &times;
          </button>
        </div>
      )}
    </div>
  );
};

// ─── Category Combobox ────────────────────────────────────────────────────────

interface CategoryComboboxProps {
  value: string;
  categories: string[];
  disabled?: boolean;
  inputRef?: React.RefObject<HTMLInputElement | null>;
  nextInputRef?: React.RefObject<HTMLInputElement | null>;
  onChange: (val: string) => void;
}

const CategoryCombobox: React.FC<CategoryComboboxProps> = ({
  value, categories, disabled, inputRef, nextInputRef, onChange,
}) => {
  const [isFocused, setIsFocused] = useState(false);

  const filtered = categories.filter(c =>
    c.toLowerCase().includes(value.toLowerCase())
  );
  const isExactMatch = categories.some(
    c => c.toLowerCase() === value.trim().toLowerCase()
  );
  const showDropdown = isFocused && (filtered.length > 0 || (value.trim().length > 0 && !isExactMatch));

  const handleSelect = (cat: string) => {
    onChange(cat);
    setIsFocused(false);
    setTimeout(() => nextInputRef?.current?.focus(), 0);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    if (showDropdown && filtered.length > 0) {
      handleSelect(filtered[0]);
    } else if (value.trim()) {
      setIsFocused(false);
      setTimeout(() => nextInputRef?.current?.focus(), 0);
    }
  };

  return (
    <div style={{ position: 'relative' }}>
      <input
        ref={inputRef}
        value={value}
        onChange={e => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setTimeout(() => setIsFocused(false), 200)}
        placeholder="e.g. Surgical"
        autoComplete="off"
        disabled={disabled}
        style={{ ...styles.input, outline: 'none', borderColor: isFocused ? '#93c5fd' : '#cbd5e1' }}
      />

      {showDropdown && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1000, backgroundColor: 'white', border: '1px solid #cbd5e1', borderRadius: '6px', marginTop: '2px', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)', maxHeight: 220, overflowY: 'auto' }}>
          {value.trim().length > 0 && !isExactMatch && (
            <div style={{ padding: '0.5rem 0.75rem', fontSize: '0.82rem', color: '#16a34a', fontWeight: 700, borderBottom: filtered.length > 0 ? '1px solid #f1f5f9' : 'none', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <span style={{ fontSize: '1rem' }}>+</span> Create &ldquo;{value.trim()}&rdquo; as new category
            </div>
          )}
          {filtered.map(cat => (
            <div
              key={cat}
              onMouseDown={e => { e.preventDefault(); handleSelect(cat); }}
              style={{ padding: '0.5rem 0.75rem', cursor: 'pointer', borderBottom: '1px solid #f1f5f9', fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}
              onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#f8fafc')}
              onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'white')}
            >
              {cat}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const initialCatalog: CatalogForm = { name: '', category: '', priority: '', gst_percent: '', low_stock_alert_quantity: '10' };
const initialBatch: BatchForm     = { vendor_name: '', batch_no: '', hsn_sac: '', purchase_inv_no: '', purchase_inv_date: '', expiry_date: '', mrp: '', dealer_price: '', selling_price: '', initial_quantity: '' };

const AddConsumableModal: React.FC<Props> = ({ onClose, onSuccess, existingConsumables = [] }) => {
  const [catalog, setCatalog]                   = useState<CatalogForm>(initialCatalog);
  const [batch, setBatch]                       = useState<BatchForm>(initialBatch);
  const [selectedExisting, setSelectedExisting] = useState<ExistingConsumable | null>(null);
  const [isSubmitting, setIsSubmitting]         = useState(false);
  const [batchPhotos, setBatchPhotos]           = useState<(File | null)[]>([null, null, null, null, null]);

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const categoryRef        = useRef<HTMLInputElement>(null);
  const gstRef             = useRef<HTMLInputElement>(null);
  const vendorRef          = useRef<HTMLInputElement>(null);
  const overlayClose       = useOverlayClose(onClose);

  const categories = Array.from(
    new Set(existingConsumables.map(c => c.category).filter(Boolean) as string[])
  ).sort((a, b) => a.localeCompare(b));

  const handleChange = (e: ChangeEvent<HTMLInputElement>) =>
    setCatalog(p => ({ ...p, [e.target.name]: e.target.value }));

  const handleBatch = (e: ChangeEvent<HTMLInputElement>) =>
    setBatch(p => ({ ...p, [e.target.name]: e.target.value }));

  const handleSelectExisting = (item: ExistingConsumable) => {
    setSelectedExisting(item);
    setCatalog({
      name: item.name,
      category: item.category ?? '',
      priority: '',
      gst_percent: item.gst_percent != null ? String(item.gst_percent) : '',
      low_stock_alert_quantity: String(item.low_stock_alert_quantity),
    });
    setTimeout(() => vendorRef.current?.focus(), 50);
  };

  const handleClearExisting = () => {
    setSelectedExisting(null);
    setCatalog(initialCatalog);
  };

  const handleSubmit = async () => {
    if (!catalog.name.trim()) {
      toast.error('Consumable name is required.');
      scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    if (!batch.initial_quantity.trim()) {
      toast.error('Initial quantity is required.');
      return;
    }
    const qty = parseInt(batch.initial_quantity);
    if (isNaN(qty) || qty < 1) {
      toast.error('Quantity must be a positive number.');
      return;
    }

    const headers = { Authorization: `Bearer ${localStorage.getItem('accessToken')}` };
    const batchPayload = {
      vendor_name:       batch.vendor_name.trim() || null,
      batch_no:          batch.batch_no.trim() || null,
      hsn_sac:           batch.hsn_sac.trim() || null,
      purchase_inv_no:   batch.purchase_inv_no.trim() || null,
      purchase_inv_date: batch.purchase_inv_date || null,
      expiry_date:       batch.expiry_date || null,
      mrp:               batch.mrp ? parseFloat(batch.mrp) : null,
      dealer_price:      batch.dealer_price ? parseFloat(batch.dealer_price) : null,
      selling_price:     batch.selling_price ? parseFloat(batch.selling_price) : null,
      initial_quantity:  qty,
      current_quantity:  qty,
    };

    setIsSubmitting(true);
    try {
      if (selectedExisting) {
        // Adding stock to an existing consumable — batch endpoint (multipart, with optional batch photos)
        const batchFormData = new FormData();
        batchFormData.append('batch_data', JSON.stringify(batchPayload));
        batchPhotos.forEach((photo, i) => { if (photo) batchFormData.append(`photo_${i + 1}`, photo); });
        await axios.post(
          `${API_URL}/api/consumables/${selectedExisting.id}/batches`,
          batchFormData,
          { headers: { ...headers, 'Content-Type': 'multipart/form-data' } },
        );
      } else {
        // Creating a new consumable + its initial batch in one atomic request
        const formData = new FormData();
        formData.append('consumable_data', JSON.stringify({
          name: catalog.name.trim(),
          category: catalog.category.trim() || null,
          priority: catalog.priority ? parseInt(catalog.priority, 10) : null,
          gst_percent: catalog.gst_percent ? parseFloat(catalog.gst_percent) : null,
          low_stock_alert_quantity: parseInt(catalog.low_stock_alert_quantity) || 10,
        }));
        formData.append('batch_data', JSON.stringify(batchPayload));
        batchPhotos.forEach((photo, i) => { if (photo) formData.append(`batch_photo_${i + 1}`, photo); });
        await axios.post(
          `${API_URL}/api/consumables/`,
          formData,
          { headers: { ...headers, 'Content-Type': 'multipart/form-data' } },
        );
      }

      toast.success(selectedExisting
        ? `Stock batch added for "${catalog.name}"!`
        : `"${catalog.name}" added to inventory!`
      );
      onSuccess();
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to save. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={styles.overlay} {...overlayClose}>
      <div style={styles.modalWrapper} onClick={e => e.stopPropagation()}>
        <div style={styles.mainArea}>
          <div style={styles.scrollArea} ref={scrollContainerRef}>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h2 style={{ margin: 0, color: '#1e293b', fontSize: '1.5rem', fontWeight: 800 }}>Register Consumable</h2>
              <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}>&times;</button>
            </div>

            {/* ── Section 1: Catalog Details ── */}
            <div style={styles.sectionBlock}>
              <h3 style={styles.sectionTitle}>1. Catalog Details</h3>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem', marginBottom: '1rem' }}>
                <label style={styles.label}>
                  Consumable Name <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <NameCombobox
                  value={catalog.name}
                  existingConsumables={existingConsumables}
                  selectedExisting={selectedExisting}
                  nextInputRef={selectedExisting ? vendorRef : categoryRef}
                  onChange={name => setCatalog(p => ({ ...p, name }))}
                  onSelectExisting={handleSelectExisting}
                  onClearExisting={handleClearExisting}
                />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem', marginBottom: '1rem' }}>
                <label style={styles.label}>Category</label>
                <CategoryCombobox
                  value={catalog.category}
                  categories={categories}
                  disabled={!!selectedExisting}
                  inputRef={categoryRef}
                  nextInputRef={gstRef}
                  onChange={category => setCatalog(p => ({ ...p, category }))}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', ...gridGap }}>
                <div style={fieldStyle}>
                  <label style={styles.label}>Priority</label>
                  <input name="priority" type="number" min="1" step="1" value={catalog.priority} onChange={handleChange} placeholder="e.g. 1" style={styles.input} disabled={!!selectedExisting} />
                </div>
                <div style={fieldStyle}>
                  <label style={styles.label}>GST % (Billing)</label>
                  <input ref={gstRef} name="gst_percent" type="number" min="0" max="100" step="0.01" value={catalog.gst_percent} onChange={handleChange} placeholder="e.g. 12" style={styles.input} disabled={!!selectedExisting} />
                </div>
                <div style={fieldStyle}>
                  <label style={styles.label}>Low Stock Alert Qty</label>
                  <input name="low_stock_alert_quantity" type="number" min="0" value={catalog.low_stock_alert_quantity} onChange={handleChange} style={styles.input} disabled={!!selectedExisting} />
                </div>
              </div>

              {selectedExisting && (
                <p style={{ fontSize: '0.75rem', color: '#94a3b8', margin: '-0.25rem 0 0', fontStyle: 'italic' }}>
                  Fields locked — existing catalog entry. Edit the item from the main table to modify these.
                </p>
              )}
            </div>

            {/* ── Section 2: Stock Batch ── */}
            <div style={styles.sectionBlock}>
              <h3 style={styles.sectionTitle}>2. {selectedExisting ? 'Refill Batch' : 'Initial Stock'}</h3>

              {/* Row 1: Vendor | Batch No. | HSN/SAC */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', ...gridGap }}>
                <div style={fieldStyle}>
                  <label style={styles.label}>Vendor / Dealer Name</label>
                  <input ref={vendorRef} name="vendor_name" value={batch.vendor_name} onChange={handleBatch} placeholder="e.g. MedSupply Co." style={styles.input} />
                </div>
                <div style={fieldStyle}>
                  <label style={styles.label}>Batch Number</label>
                  <input name="batch_no" value={batch.batch_no} onChange={handleBatch} placeholder="e.g. BT-2024-001" style={styles.input} />
                </div>
                <div style={fieldStyle}>
                  <label style={styles.label}>HSN / SAC Code</label>
                  <input name="hsn_sac" value={batch.hsn_sac} onChange={handleBatch} placeholder="e.g. 30049099" style={styles.input} />
                </div>
              </div>

              {/* Row 2: Invoice No. | Invoice Date | Expiry Date */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', ...gridGap }}>
                <div style={fieldStyle}>
                  <label style={styles.label}>Purchase Invoice No.</label>
                  <input name="purchase_inv_no" value={batch.purchase_inv_no} onChange={handleBatch} placeholder="e.g. INV-5467" style={styles.input} />
                </div>
                <div style={fieldStyle}>
                  <label style={styles.label}>Purchase Invoice Date</label>
                  <input name="purchase_inv_date" type="date" value={batch.purchase_inv_date} onChange={handleBatch} style={styles.input} />
                </div>
                <div style={fieldStyle}>
                  <label style={styles.label}>Expiry Date</label>
                  <input name="expiry_date" type="date" value={batch.expiry_date} onChange={handleBatch} style={styles.input} />
                </div>
              </div>

              {/* Row 3: MRP | Dealer Price | Selling Price | Qty Received */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', ...gridGap }}>
                <div style={fieldStyle}>
                  <label style={styles.label}>MRP (₹)</label>
                  <input name="mrp" type="number" min="0" step="0.01" value={batch.mrp} onChange={handleBatch} placeholder="0.00" style={styles.input} />
                </div>
                <div style={fieldStyle}>
                  <label style={styles.label}>Dealer Price (₹)</label>
                  <input name="dealer_price" type="number" min="0" step="0.01" value={batch.dealer_price} onChange={handleBatch} placeholder="0.00" style={styles.input} />
                </div>
                <div style={fieldStyle}>
                  <label style={styles.label}>Selling Price (₹)</label>
                  <input name="selling_price" type="number" min="0" step="0.01" value={batch.selling_price} onChange={handleBatch} placeholder="0.00" style={styles.input} />
                </div>
                <div style={fieldStyle}>
                  <label style={styles.label}>Qty Received <span style={{ color: '#ef4444' }}>*</span></label>
                  <input name="initial_quantity" type="number" min="1" value={batch.initial_quantity} onChange={handleBatch} placeholder="e.g. 100" style={{ ...styles.input, fontWeight: 700 }} />
                </div>
              </div>

              {/* Row 4: Batch Photos — for new consumables and refill batches alike */}
              <div style={{ marginTop: '0.9rem' }}>
                <label style={{ ...styles.label, marginBottom: '0.5rem' }}>
                  {selectedExisting ? 'Batch Photos' : 'Initial Batch Photos'}{' '}
                  <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0, color: '#94a3b8' }}>(optional)</span>
                </label>
                <PhotoUploadGrid files={batchPhotos} label="Batch Photos" onChange={setBatchPhotos} />
              </div>
            </div>

          </div>

          {/* ── Footer ── */}
          <div style={styles.footer}>
            <button type="button" onClick={onClose} style={{ ...styles.button, ...styles.cancelButton }}>
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isSubmitting}
              style={{ ...styles.button, ...styles.saveButton, opacity: isSubmitting ? 0.7 : 1, cursor: isSubmitting ? 'not-allowed' : 'pointer' }}
            >
              {isSubmitting ? 'Saving...' : selectedExisting ? 'Add Stock Batch' : 'Save & Add Stock'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddConsumableModal;
